import React, { useMemo } from 'react';
import { MetricCard } from '../MetricCard';
import { PopulationVsHousingGrowthChart } from './PopulationVsHousingGrowthChart';
import { HousingStructurePieChart } from './HousingStructurePieChart';
import { HousingByYearChart } from './HousingByYearChart';
import { HousingUnitBreakdownRecord } from '@/hooks/useHousingUnitBreakdownData';
import { HousingStockGrowthRecord } from '@/hooks/useHousingStockGrowthData';
import { formatPlaceName } from '@/lib/utils';
import { useGrowthChartData } from '@/hooks/useGrowthChartData';
import StackUpAnalysis from './StackUpAnalysis';
import { useFreshPermittingData } from '@/hooks/useFreshPermittingData';

interface CitySnapshotDashboardProps {
  selectedPlace: {
    Place: string;
    place_fips: string;
  } | null;
  benchmarkingRecords: Record<string, any>[];
  housingUnitBreakdownRecords: HousingUnitBreakdownRecord[];
  housingStockGrowthRecords: HousingStockGrowthRecord[];
}

const CitySnapshotDashboard: React.FC<CitySnapshotDashboardProps> = ({ selectedPlace, benchmarkingRecords, housingUnitBreakdownRecords, housingStockGrowthRecords }) => {

  const { data: growthChartData, loading: growthChartLoading } = useGrowthChartData(selectedPlace?.Place || null, selectedPlace?.place_fips || null);
  const { data: freshPermitData, loading: permitLoading } = useFreshPermittingData(selectedPlace?.Place || null, selectedPlace?.place_fips || null);
  const latestRecord = benchmarkingRecords[0] || {};

  const dashboardData = useMemo(() => {
    
    if (!benchmarkingRecords || benchmarkingRecords.length === 0 || !housingStockGrowthRecords || housingStockGrowthRecords.length === 0) {
      return { topMetrics: [], bottomMetrics: [], pieData: [], barData: [] };
    }

    const previousRecord = benchmarkingRecords[1] || {};

    const latestGrowthRecord = housingStockGrowthRecords?.[0];
    const previousGrowthRecord = housingStockGrowthRecords?.[1];

    const safeNum = (num: any) => (typeof num === 'number' ? num : 0);
    const formatPercent = (val: any, decimals = 1) => `${(safeNum(val) * 100).toFixed(decimals)}%`;
    const formatTrend = (current: any, prev: any, suffix = '', decimals = 1, isPercent = false) => {
      const currentNum = safeNum(current);
      const prevNum = safeNum(prev);
      
      if (currentNum === prevNum || isNaN(currentNum) || isNaN(prevNum)) return "";
      
      const sign = currentNum > prevNum ? '+' : '';
      
      if (isPercent) {
        if (prevNum === 0) return ""; // Avoid division by zero, nulling out the trend
        const growth = ((currentNum - prevNum) / prevNum) * 100;
        return `${sign}${growth.toFixed(decimals)}%`;
      }
      
      const diff = currentNum - prevNum;
      const formattedDiff = diff.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
      return `${sign}${formattedDiff}${suffix}`;
    };

    const housingGrowthValue = () => {
      const growth = latestGrowthRecord?.housing_stock_growth_if_permits_built_instantly;
      if (safeNum(latestRecord.total_population) < 2000) {
        if (growth >= -100 && growth <= 1000) {
          return `${safeNum(growth).toFixed(1)}%`;
        } else {
          return "Population too small.";
        }
      }
      return `${safeNum(growth).toFixed(1)}%`;
    };

    const latestPermitRecord = freshPermitData;

    const topMetrics = [
      {
        title: "Total Population",
        description: `Estimated population in ${formatPlaceName(selectedPlace?.Place || '')} as of ${latestRecord.year}`,
        value: (safeNum(latestRecord.total_population).toLocaleString() || 'N/A' ) + " persons",
        trend: formatTrend(latestRecord.total_population, previousRecord.total_population, ' persons', 0),
        icon: "👤"
      },
      {
        title: "Housing Stock Growth",
        description: `Percentage increase in number of housing units in ${formatPlaceName(selectedPlace?.Place || '')} if all permitted units were built instantly as of ${latestRecord.year}`,
        value: housingGrowthValue(),
        trend: formatTrend(latestGrowthRecord?.housing_stock_growth_if_permits_built_instantly, previousGrowthRecord?.housing_stock_growth_if_permits_built_instantly, '%', 1, true),
        hideTrend: true,
        icon: "🏗️",
      },
      {
        title: "Total Units Permitted but Unbuilt",
        description: `Total units permitted in ${formatPlaceName(selectedPlace?.Place || '')} that are not yet recorded as built as of ${latestPermitRecord?.year ?? latestRecord.year}`,
        value: safeNum(latestPermitRecord?.bps_total_units).toLocaleString() + " units",
        trend: "",
        hideTrend: true,
        icon: "📝"
      }
    ];

    const bottomMetrics = [
      {
        title: "Price to Income Ratio",
        description: `Median home value to median household income ratio in ${formatPlaceName(selectedPlace?.Place || '')} as of ${latestRecord.year}`,
        value: `${safeNum(latestRecord.price_to_income).toFixed(1)}x`,
        trend: formatTrend(latestRecord.price_to_income, previousRecord.price_to_income, 'x'),
        icon: "💰",
        invertTrendColor: true
      },
      {
        title: "Multi=Family Share",
        description: `Percentage of housing units in multi-family (2+ unit) structures in ${formatPlaceName(selectedPlace?.Place || '')} as of ${latestRecord.year}`,
        value: formatPercent(safeNum(latestRecord.pct_mf_existing) / 100),
        trend: formatTrend(safeNum(latestRecord.pct_mf_existing), safeNum(previousRecord.pct_mf_existing), '', 1, true),
        icon: "🏢"
      },
      {
        title: "Rental Vacancy Rate",
        description: `Percentage of rental units that are vacant in ${formatPlaceName(selectedPlace?.Place || '')} as of ${latestRecord.year}`,
        value: formatPercent(safeNum(latestRecord.rental_vacancy_rate) / 100),
        trend: formatTrend(safeNum(latestRecord.rental_vacancy_rate), safeNum(previousRecord.rental_vacancy_rate), '', 1, true),
        icon: "📉",
        invertTrendColor: true
      },
    ];

    const latestBreakdown = housingUnitBreakdownRecords?.[0] || {};
    const previousBreakdown = housingUnitBreakdownRecords?.[1] || {};
    
    const pieDataRaw = [
      { name: '1-Unit Detached', value: safeNum(latestBreakdown['1_unit_detached']), prevValue: safeNum(previousBreakdown['1_unit_detached']) },
      { name: '1-Unit Attached', value: safeNum(latestBreakdown['1_unit_attached']), prevValue: safeNum(previousBreakdown['1_unit_attached']) },
      { name: '2 Units', value: safeNum(latestBreakdown['2_unit_structures']), prevValue: safeNum(previousBreakdown['2_unit_structures']) },
      { name: '3-4 Units', value: safeNum(latestBreakdown['3_4_unit_structures']), prevValue: safeNum(previousBreakdown['3_4_unit_structures']) },
      { name: '5-9 Units', value: safeNum(latestBreakdown['5_9_unit_structures']), prevValue: safeNum(previousBreakdown['5_9_unit_structures']) },
      { name: '10-19 Units', value: safeNum(latestBreakdown['10_19_unit_structures']), prevValue: safeNum(previousBreakdown['10_19_unit_structures']) },
      { name: '20-49 Units', value: safeNum(latestBreakdown['20_49_unit_structures']), prevValue: safeNum(previousBreakdown['20_49_unit_structures']) },
      { name: '50+ Units', value: safeNum(latestBreakdown['50_plus_unit_structures']), prevValue: safeNum(previousBreakdown['50_plus_unit_structures']) },
    ];

    const totalUnits = pieDataRaw.reduce((sum, item) => sum + item.value, 0);
    
    const pieData = pieDataRaw.map(item => {
      const change = item.value - item.prevValue;
      const percentChange = item.prevValue > 0 ? (change / item.prevValue) * 100 : 0;
      return {
        name: item.name,
        value: item.value,
        percent: totalUnits > 0 ? (item.value / totalUnits) : 0,
        percentChange: percentChange,
      }
    }).filter(d => d.value > 0);

    const barData = [
        { year: 'Before 1960', units: safeNum(latestRecord.units_built_1939_or_earlier) + safeNum(latestRecord.units_built_between_1940_and_1949) + safeNum(latestRecord.units_built_between_1950_and_1959) },
        { year: '1960-79', units: safeNum(latestRecord.units_built_between_1960_and_1969) + safeNum(latestRecord.units_built_between_1970_and_1979) },
        { year: '1980-99', units: safeNum(latestRecord.units_built_between_1980_and_1989) + safeNum(latestRecord.units_built_between_1990_and_1999) },
        { year: '2000-09', units: safeNum(latestRecord.units_built_between_2000_and_2009) },
        { year: '2010+', units: safeNum(latestRecord.units_built_between_2010_and_2013) + safeNum(latestRecord.units_built_2014_or_later) },
    ].filter(d => d.units > 0);

    return { topMetrics, bottomMetrics, pieData, barData };
  }, [benchmarkingRecords, housingUnitBreakdownRecords, housingStockGrowthRecords, selectedPlace, freshPermitData]);

  if (!selectedPlace) {
    return null;
  }

  const isLoading = !benchmarkingRecords || benchmarkingRecords.length === 0 || permitLoading;

  const renderContent = (chartName: React.ReactNode, chartDescription: React.ReactNode | null, Component: React.ElementType, data: any[], chartLoading?: boolean) => (
    <div className="bg-white/70 backdrop-blur-sm p-4 rounded-xl border border-slate-200/50 min-h-[400px] flex flex-col h-full">
      <div className='mb-4'>
        <h3 className="font-semibold text-slate-900 text-base">{chartName}</h3>
        {chartDescription && <p className="text-sm text-slate-500 font-normal">{chartDescription}</p>}
      </div>
      {chartLoading ? (
        <div className="flex-grow flex items-center justify-center text-slate-500">Loading...</div>
      ) : (
        <Component data={data} />
      )}
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      {isLoading ? (
        Array(9).fill(0).map((_, i) => <div key={i} className="h-40 bg-slate-100 rounded-xl animate-pulse"></div>)
      ) : (
        <>
          {/* Top Metrics */}
          {dashboardData.topMetrics.map((metric, index) => (
            <div key={metric.title} className="h-40 lg:col-span-1">
              <MetricCard {...metric} delay={index * 0.1} />
            </div>
          ))}

          {/* How Does {City} Stack Up? Analysis */}
          <div className="lg:col-span-1">
            <StackUpAnalysis
              selectedPlace={selectedPlace}
              growthChartData={growthChartData}
              loading={growthChartLoading}
            />
          </div>

          {/* Population vs Housing Growth Chart */}
          <div className="lg:col-span-2">
              {renderContent(
                <span>
                  <span className="font-bold block">
                    Is your population's housing future <span className="text-green-600">covered</span> or <span className="text-red-600">under stress</span>?
                  </span>
                </span>,
                `Check out the chart below to see how the housing stock growth compares to the population growth in ${formatPlaceName(selectedPlace?.Place || '')} from ${latestRecord.year - 1} to ${latestRecord.year}`,
                PopulationVsHousingGrowthChart,
                growthChartData,
                growthChartLoading
              )}
          </div>

          {/* Bottom Metrics */}
          {dashboardData.bottomMetrics.map((metric, index) => (
            <div key={metric.title} className="lg:col-span-1">
              <MetricCard {...metric} delay={index * 0.1} />
            </div>
          ))}
          
          {/* Housing Stock by Year Built */}
          <div className="lg:col-span-2">
            {renderContent(
              "Housing Stock by Year Built as of " + latestRecord.year, 
              `Existing housing structures broken down by decade built in ${formatPlaceName(selectedPlace?.Place || '')} as of ${latestRecord.year}`,
              HousingByYearChart, 
              dashboardData.barData, 
              isLoading
            )}
          </div>

          {/* Housing by Units in Structure */}
          <div className="lg:col-span-1">
            {renderContent(
              "Housing by Units in Structure in " + latestRecord.year, 
              `Existing housing structures broken down by number of units in the structure in ${formatPlaceName(selectedPlace?.Place || '')} as of ${latestRecord.year} Click a band to see the category name, number of units in that category, that categories share of total units and the change in share.`,
              HousingStructurePieChart, 
              dashboardData.pieData, 
              isLoading)}
          </div>
        </>
      )}
    </div>
  );
};

export default CitySnapshotDashboard; 